import { useCallback, useEffect } from "react";

type EventHandler<T> = (param: T) => void;

function createEvent<T>() {
  const listeners = new Set<EventHandler<T>>();

  const emit = (param: T): void => {
    Array.from(listeners).forEach((listener) => listener(param));
  };

  const on = (listener: EventHandler<T>) => {
    listeners.add(listener);

    return {
      cancel: () => {
        listeners.delete(listener);
      },
    };
  };

  return {
    on,
    emit,
  };
}

const eventBus = createEvent<EventType>();

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type EventType<Type extends string = string, Data extends object = {}> = {
  type: Type;
} & Data;

export type Dispatch<E extends EventType> = (data: Omit<E, "type">) => void;

export function useEvent<E extends EventType>(type: E["type"], onEvent?: (event: E) => void): Dispatch<E> {
  useEffect(() => {
    if (!onEvent) return;
    const subscription = eventBus.on((event) => {
      if (event.type === type) {
        onEvent(event as E);
      }
    });

    return () => {
      subscription.cancel();
    };
  }, [type, onEvent]);

  return useCallback(
    (data: Omit<E, "type">) => {
      eventBus.emit({ type, ...data });
    },
    [type],
  );
}
